/* eslint-disable @typescript-eslint/naming-convention */

import { useState } from 'react';
import styled from '@emotion/styled';
import {
  IcoBookmark,
  IcoLuggage,
  IcoMedal,
  IcoMessage,
  IcoNotification,
  IcoSupportAgent,
  IcoVisibility,
} from '@myrealtrip/web-icons';

import { sendClickLog } from '../../utils/sendClickLog';
import { WeblogPageCategory, WeblogScreenName } from '../../../shared/constants/weblog';
import { toCurrency } from '../../../shared/utils/toCurrency';

import NotificationPopup from '../../components/NotificationPopup';
import ProfileCardButton from '../../components/ProfileCardButton';
import useFetchHasFreshMessage from '../../hooks/useFetchHasFreshMessage';
import useSignOut from '../../hooks/useSignOut';
import { getTravelerLink } from '../../utils/getTravelerLink';
import { useLayoutHeaderContext } from '../../../modules/LayoutHeaderContext';
import { globalVariables } from '../../../shared/designSystem/constants';
import DomainManager from '../../../shared/utils/DomainManager';

import SideBarLink from './SideBarLink';
import { useEnvContext } from '../../../modules/EnvProvider';

const ICON_SIZE = 24;

const baseLogData = {
  screenName: WeblogScreenName.COMMON,
  pageCategory: WeblogPageCategory.COMMON,
};

export default function LoggedInItems() {
  const { isFlightMode, user } = useLayoutHeaderContext();
  const { handleSignOut } = useSignOut();
  const { targetEnv } = useEnvContext();
  const domainManager = new DomainManager(targetEnv);
  const memberDomain = domainManager.getMemberServiceDomain();
  const supportDomain = domainManager.getSupportDomain();

  const { numberOfCoupons, point } = user ?? {};
  const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false);

  const { data: freshMessageData, isError: isFreshMessageError } = useFetchHasFreshMessage();
  const hasFreshMessage = freshMessageData?.new_messages && !isFreshMessageError;

  const handleNotificationOpen = () => {
    sendClickLog({
      ...baseLogData,
      eventName: 'topmenu',
      itemKind: 'notification',
      itemName: '알림',
    });

    setIsNotificationPopupOpen(true);
  };
  const handleNotificationClose = () => setIsNotificationPopupOpen(false);

  const pointAmount = isFlightMode ? '' : `${toCurrency(String(point))}원`;
  const couponAmount = isFlightMode ? '' : `${toCurrency(String(numberOfCoupons))}장`;

  const handleClickSupport = () => {
    sendClickLog({
      ...baseLogData,
      eventName: 'topmenu',
      itemKind: 'menu',
      itemName: '고객지원실',
      targetUrl: supportDomain,
    });

    location.href = supportDomain;
  };

  return (
    <Container>
      <>
        <Section>
          <ProfileCardButton showDetail />
          {Boolean(user) && (
            <UserInfo>
              <PointItem href={`${memberDomain}/point`}>
                <UserInfoTitle>포인트</UserInfoTitle>
                {pointAmount && <UserInfoDesc>{pointAmount}</UserInfoDesc>}
              </PointItem>
              <CouponItem href={`${memberDomain}/coupon`}>
                <UserInfoTitle>쿠폰</UserInfoTitle>
                {couponAmount && <UserInfoDesc>{couponAmount}</UserInfoDesc>}
              </CouponItem>
            </UserInfo>
          )}
        </Section>
        <Section>
          <SideBarLink
            label="내 회원 등급"
            icon={<IcoMedal size={ICON_SIZE} />}
            link={`${memberDomain}/grade-benefit`}
          />
        </Section>
        <Section>
          <SideBarLink
            label="최근 본 상품"
            icon={<IcoVisibility size={ICON_SIZE} />}
            link={getTravelerLink(
              '/search/theme?themeKeyName=recentViewProduct&seemorePage=seemore_mypage_recent&apiType=user',
              targetEnv,
            )}
          />
          <SideBarLink
            label="저장됨"
            icon={<IcoBookmark size={ICON_SIZE} />}
            link={`${memberDomain}/wish/groups`}
          />
          <SideBarLink
            label="내 여행"
            icon={<IcoLuggage size={ICON_SIZE} />}
            link={getTravelerLink('/reservations', targetEnv)}
          />
          <SideBarLink
            label="메시지"
            icon={<IcoMessage size={ICON_SIZE} />}
            link={getTravelerLink('/messenger/channels', targetEnv)}
            redDot={hasFreshMessage}
          />
          {!isFlightMode && (
            <SideBarLink
              label="알림"
              icon={<IcoNotification size={ICON_SIZE} />}
              onClick={handleNotificationOpen}
            />
          )}
        </Section>
        <Section>
          <SideBarLink
            label="고객지원실"
            icon={<IcoSupportAgent size={ICON_SIZE} />}
            onClick={handleClickSupport}
          />
        </Section>
        <Section noBorder>
          <SideBarLink label="로그아웃" onClick={handleSignOut} />
        </Section>
      </>
      {isNotificationPopupOpen && (
        <NotificationPopup isOpen={isNotificationPopupOpen} onClose={handleNotificationClose} />
      )}
    </Container>
  );
}
const Container = styled.div``;

const UserInfo = styled.div`
  display: flex;
  margin-top: 15px;
`;
const PointItem = styled.a`
  display: inline-flex;
  width: 60%;
`;
const CouponItem = styled.a`
  display: inline-flex;
  width: 40%;
`;
const UserInfoTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${globalVariables.gray_500};
`;
const UserInfoDesc = styled.span`
  margin-left: 4px;
  font-size: 14px;
  font-weight: 500;
  color: ${globalVariables.blue_500};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Section = styled.div<{ noBorder?: boolean }>`
  padding: 20px 16px;
  border-bottom: ${({ noBorder }) => (noBorder ? '0' : `1px solid ${globalVariables.gray_100}`)};
`;
